import Controller, { inject as controller } from '@ember/controller';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import FeaturesService from 'ember-feature-flags/services/features';
import ApiErrorsService from 'cio/services/api-errors';
import SignupService from 'cio/services/signup';
import AnalyticsService from 'cio/services/analytics';
import PostRegistrationController from 'cio/pods/authenticated/post-registration/controller';
import SignupController from 'cio/pods/signup/controller';
import fade from 'ember-animated/transitions/fade';
import { storageFor } from 'ember-local-storage';
import AuthService from 'cio/services/auth';
import DS from 'ember-data';
import RouterService from '@ember/routing/router-service';
import AccountService from 'cio/services/account';
import moment from 'moment-timezone';
import { ProductApp } from 'cio/utils/product-app-info';
import ExperimentsService from 'cio/services/experiments';

const HEARD_ABOUT_US_ITEMS = [
  { value: 'partner', label: 'Recommended by a vendor, service provider or agency' },
  { value: 'startup', label: 'Recommended by an investor or incubator' },
  { value: 'google', label: 'A search engine (like Google)' },
  { value: 'segment', label: 'Segment (previously Segment.io)' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'article', label: 'A blog post / article online' },
  { value: 'customer', label: 'Recommended by a current/former CIO customer' },
  { value: 'repeat', label: "I've used Customer.io before" },
  { value: 'other', label: 'Other' },
];

export default class PostRegistrationReadyController extends Controller {
  @service features!: FeaturesService;
  @service account!: AccountService;
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service signup!: SignupService;
  @service store!: DS.Store;
  @service router!: RouterService;
  @service experiments!: ExperimentsService;

  @storageFor('cio-alerts') localStorage: any;

  @controller('authenticated/post-registration') postRegistrationController!: PostRegistrationController;
  @controller('signup') signupController!: SignupController;

  _formValues: Partial<{
    heardAboutUs: string;
    heardAboutUsWhichPartner: string;
    heardAboutUsMoreInfo: string;
  }> = {
    heardAboutUs: '',
  };

  _heardAboutUsItems = Object.freeze([{ value: '', label: 'Choose one…', disabled: true }, ...HEARD_ABOUT_US_ITEMS]);

  fade = fade;

  // Measures the time it takes from the user viewing this page to the account being ready.
  accountReadyDelay = 0;
  async measureAccountReadyDelay() {
    if (this.postRegistrationController.waitForAccountToBeReadyTask.isRunning) {
      let start = window.performance.now();

      await this.postRegistrationController.waitForAccountToBeReadyTask.lastRunning;

      let elapsed = window.performance.now() - start;
      this.accountReadyDelay = elapsed;
    }
  }

  _submitTask = task(async () => {
    let start = window.performance.now();
    let comments =
      this._formValues.heardAboutUs === 'partner'
        ? this._formValues.heardAboutUsWhichPartner
        : this._formValues.heardAboutUsMoreInfo;

    try {
      await this.signup.postRegistration({
        selectedFeedbackOption: this._formValues.heardAboutUs,
        comments,
        useCase: this.signupController.formValues.useCase,
        useCaseDetail: this.signupController.formValues.useCaseDetail,
      });

      // Only save the use case to the setup guide if Journeys is enabled. For
      // CDP-only accounts this step isn't relevant, because the setup guide
      // referenced here is the Journeys one
      if (
        (this.account.current?.productActivations ?? []).includes(ProductApp.JOURNEYS) &&
        this.signupController.formValues.useCase
      ) {
        await this.postRegistrationController.updateSetupGuideTask.perform(
          'useCase',
          this.signupController.formValues.useCase,
        );
        await this.postRegistrationController.updateSetupGuideTask.perform('startedAt', moment().unix());
      }

      await this.analytics.track('signup', 'signup_completed', {
        signupType: 'journeys',
        fakeDoorExperiment: true,
        campaignTemplateGalleryExperiment: this.experiments.getExperimentVariation('campaignTemplateGalleryExperiment'),
        testDeliveryMode: this.signupController?.formValues?.testDeliveryMode,
      });
      await this.analytics.track('signup', 'referral_source_submitted', {
        referrer: this._formValues.heardAboutUs,
        comments,
        signupType: this.signupController.model?.signupType.name ?? '',
      });
    } catch (err) {
      this.apiErrors.report(err);
      throw err;
    }

    this.localStorage.set('multi-id-workspaces-overview', false);

    let goToAccountDelay = window.performance.now() - start + this.accountReadyDelay;

    let lastAccountReadyTask = await this.postRegistrationController.waitForAccountToBeReadyTask.lastSuccessful;
    let accountReadyDuration = lastAccountReadyTask?.elapsedTime ?? 0;

    this.analytics.track('performance', 'signup', {
      goToAccountDelay,
      accountReadyDuration,
      signupType: this.signupController.model?.signupType?.name,
    });

    this.router.transitionTo('app');
  });
}
